import React, { ChangeEvent, FC, useCallback } from 'react';
import { connect } from 'react-redux';

import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { ToggleSize } from 'app/design-system/components/Toggle';
import { useTranslation } from 'app/lang';

import { ChangeStakeTypeAmount, changeStakeTypeAmount } from '../../actions/stakeTypes';
import { AmountInputModifier, SubmissionState } from '../../constants';
import { RecordOfStakeTypeAmountType } from '../../records';
import { selectAnyHasLockedState, selectSomeHasError } from '../../selectors/entries';
import { selectAmountStakeType } from '../../selectors/stakeTypes';
import { selectSystemAmountModifier } from '../../selectors/stakeTypes/system';
import { selectSubmission } from '../../selectors/submission';
import InitialToggle from './Initial';

import dictionary from './Toggle.dictionary.json';

// ---

type StoreProps = {
  amount: RecordOfStakeTypeAmountType;
  modifier: AmountInputModifier;
  disabled: boolean;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<ChangeStakeTypeAmount>;
};

type SumOfIndividualExpressToggleProps = {
  size?: ToggleSize;
  className?: string;
};

const SumOfIndividualExpressToggle: FC<
  SumOfIndividualExpressToggleProps & StoreProps & DispatchProp
> = ({
  amount,
  modifier,
  disabled,
  dispatch,
  size,
  className,
}) => {
  const { t } = useTranslation();
  const switchToggleHandler = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const modifier = event.target.checked
      ? AmountInputModifier.MULTIPLY
      : AmountInputModifier.DIVIDE;

    dispatch(changeStakeTypeAmount(amount.value, amount.account, modifier));
  }, [amount]);

  return (
    <InitialToggle active={modifier === AmountInputModifier.MULTIPLY}
                   disabled={disabled}
                   data-test="sumOfIndividualExpress-toggle"
                   title={t(dictionary.sumOfIndividualExpressTitle)}
                   size={size}
                   className={className}
                   onChange={switchToggleHandler} />
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const submission = selectSubmission(state);

  const hasEntriesErrors = selectSomeHasError(state) || selectAnyHasLockedState(state);

  return {
    amount: selectAmountStakeType(state),
    modifier: selectSystemAmountModifier(state),
    disabled: submission !== SubmissionState.AWAITING || hasEntriesErrors,
  };
};

export default connect(mapStateToProps)(SumOfIndividualExpressToggle);
